import type { PillsToolbarItem } from '../FilterToolbar'

import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { FILTER_ALL } from './use-list-filters'

/**
 * The leading pill that clears every filter at once. A one-option pill group
 * that reads as selected while nothing is filtered.
 *
 * `isCleared` is passed in rather than derived from `hasActiveFilters`: on a
 * screen whose default is itself a filter, clearing everything and resetting
 * to the default are two different states, and this pill shows the former.
 */
export function clearAllPillItem(
  isCleared: boolean,
  onClear: () => void,
): PillsToolbarItem<boolean> {
  return {
    type: 'pills',
    options: [{ value: true, label: CONTROLS.filter.all }],
    value: isCleared,
    onChange: onClear,
  }
}

/**
 * A pill that is on or off: a one-option group that reads as selected while
 * the filter it stands for is on, and switches it back when pressed again.
 *
 * Beside `clearAllPillItem`, which is the same shape for an action rather
 * than a state — pressing that one twice clears twice, pressing this one
 * twice is where it started.
 */
export function togglePillItem(
  label: string,
  value: boolean,
  onChange: (value: boolean) => void,
): PillsToolbarItem<boolean> {
  return {
    type: 'pills',
    options: [{ value: true, label }],
    value,
    onChange: () => onChange(!value),
  }
}

/** The option every select leads with: the sentinel and the word for it, which
 *  have to agree wherever a filter offers them. */
// `as const` so the value stays the literal: a filter typed over its own
// members takes this option only when it is one of them.
export const ALL_OPTION = {
  value: FILTER_ALL,
  label: CONTROLS.filter.all,
} as const
